// ══════════════════════════════════════════════════════════════════
// supabase.js — La Conciergerie
// Auth, store (get/set), uploads, useSt hook
// Extrait du Script 1 de index.html (migration Option A → Option B Vite)
// ══════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { confirmByRecordIds } from './localQueue.js';
import { SUPA_URL, SUPA_KEY, VAPID_PUBLIC_KEY, CLIENT_APP_URL } from './constants.js';
import { realtimeBus } from './realtime.js';
import { compressToDataUrl, dataUrlSizeKB } from './utils.js';

// ══════════════ VAPID HELPER ══════════════
export function urlBase64ToUint8Array(base64String){
  const padding='='.repeat((4-base64String.length%4)%4);
  const base64=(base64String+padding).replace(/-/g,'+').replace(/_/g,'/');
  const rawData=window.atob(base64);
  const outputArray=new Uint8Array(rawData.length);
  for(let i=0;i<rawData.length;++i)outputArray[i]=rawData.charCodeAt(i);
  return outputArray;
}

// ══════════════ AUTH — tokens & session ══════════════

// ══════════════ AUTH (token utilisateur) ══════════════
// Le token est stocké en mémoire + localStorage pour survivre aux rechargements.
// Si pas de token → on retombe sur la clé anon (cas avant connexion).
export let SUPA_TOKEN=null;
export let SUPA_REFRESH=null;
try{
  const saved=localStorage.getItem('supa_session');
  if(saved){const s=JSON.parse(saved);SUPA_TOKEN=s.access_token||null;SUPA_REFRESH=s.refresh_token||null;}
}catch{}
export function supaAuthHeader(){return SUPA_TOKEN||SUPA_KEY;}
/**
 * (v220) Écrit une clé du module Consigne via la fonction serveur
 * `consigne-ecrire-anonyme` (clé de service, contourne le blocage
 * d'écriture anonyme rencontré côté RLS/GRANT — cause exacte non isolée
 * avec certitude malgré policies + GRANT posés, HTTP 401 persistant).
 * Utilisée par l'écran comptoir (Dépôt/Retrait/Retour/Registre), qui reste
 * délibérément SANS connexion. Lève une erreur explicite (jamais un
 * plantage silencieux) si la fonction serveur refuse ou est inatteignable.
 */
export async function supaEcrireConsigneAnonyme(cle, valeur){
  // (v221) CORRECTIF — la passerelle Supabase exige un en-tête Authorization
  // même quand "Verify JWT" est désactivé pour la fonction ; son absence
  // provoquait un rejet "UNAUTHORIZED_NO_AUTH_HEADER" avant même d'atteindre
  // le code de la fonction. La clé anonyme suffit (supaAuthHeader() y
  // retombe toujours sur cet écran, sans connexion — voir forcerAnonymeMemoire).
  return _appelerConsigneEcrire({ cle, mode: 'remplacer', valeur });
}

/**
 * (v224) Ajoute UN élément à la fin du tableau stocké sous `cle`, de façon
 * ATOMIQUE côté Postgres (fonction app_data_append) — plus de lecture-puis-
 * écriture côté client, donc plus de course possible entre deux ajouts
 * simultanés (deux onglets, deux membres du personnel). À préférer à
 * supaEcrireConsigneAnonyme(cle, [...arr, element]) partout où c'est un
 * simple ajout (nouveau dépôt, nouvelle ligne de CA).
 */
export async function supaEcrireConsigneAjouter(cle, element){
  return _appelerConsigneEcrire({ cle, mode: 'ajouter', element });
}

/**
 * (v224) Fusionne `patch` dans l'élément du tableau dont le champ "id" vaut
 * `id`, de façon ATOMIQUE (fonction app_data_patch_by_id) — même principe
 * que ci-dessus, pour honorer une résa, clôturer un bagage, patcher un
 * retour, rectifier une fiche RGPD.
 */
export async function supaEcrireConsignePatch(cle, id, patch){
  return _appelerConsigneEcrire({ cle, mode: 'patch', id, patch });
}

async function _appelerConsigneEcrire(corps){
  const r = await fetch(`${SUPA_URL}/functions/v1/consigne-ecrire-anonyme`, {
    method: 'POST',
    headers: { apikey: SUPA_KEY, 'Authorization': `Bearer ${supaAuthHeader()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(corps),
  });
  if (!r.ok) {
    let texte = ''; try { texte = await r.text(); } catch {}
    throw new Error('consigne-ecrire-anonyme HTTP '+r.status+' for key '+corps.cle+(texte?(' — '+texte.slice(0,300)):''));
  }
  const data = await r.json().catch(() => ({}));
  if (!data?.ok) throw new Error('consigne-ecrire-anonyme refusé pour '+corps.cle+' — '+(data?.error||'raison inconnue'));
}
/**
 * (v219) Force CETTE instance (cet onglet) à retomber sur la clé anonyme,
 * SANS toucher localStorage — contrairement à supaSaveSession(null). Pensé
 * pour l'écran comptoir (route publique, sans connexion) : si l'appareil a
 * déjà servi à se connecter à l'appli principale, un jeton (potentiellement
 * expiré) traîne dans localStorage['supa_session'] et serait repris ici par
 * erreur (401 sur les écritures). Effacer ce jeton en mémoire, dans CETTE
 * page seulement, corrige le problème sans jamais déconnecter un onglet
 * admin resté ouvert à côté sur le même navigateur (localStorage est
 * partagé entre onglets, la mémoire JS ne l'est pas).
 */
export function forcerAnonymeMemoire(){SUPA_TOKEN=null;SUPA_REFRESH=null;}
// ══════════════ DÉTECTION DU RETOUR D'EMAIL (recovery) ══════════════
// Quand un client clique le lien « Définir mon mot de passe » reçu par email,
// Supabase le renvoie ici avec un fragment d'URL du type :
//   #access_token=...&refresh_token=...&type=recovery
// On capture ce jeton AU CHARGEMENT (avant tout rendu React), on ouvre la session
// correspondante, on nettoie l'URL (sécurité : le jeton ne doit pas rester visible),
// et on lève un drapeau pour afficher l'écran de définition de mot de passe.
export let RECOVERY_MODE=false;
(function detectRecovery(){
  try{
    const hash=window.location.hash||'';
    if(!hash||hash.indexOf('access_token=')===-1)return;
    const params=new URLSearchParams(hash.replace(/^#/,''));
    const type=params.get('type');
    const at=params.get('access_token');
    const rt=params.get('refresh_token');
    // On ne traite que les retours de type recovery (définition / réinitialisation de MDP).
    if(type==='recovery'&&at){
      SUPA_TOKEN=at;
      SUPA_REFRESH=rt||null;
      try{localStorage.setItem('supa_session',JSON.stringify({access_token:at,refresh_token:rt,expires_at:params.get('expires_at')}));}catch{}
      RECOVERY_MODE=true;
      // Nettoyage de l'URL : on retire le fragment pour ne pas exposer le jeton.
      try{history.replaceState(null,'',window.location.pathname+window.location.search);}catch{}
    }
  }catch(e){console.warn('[recovery] détection échouée',e);}
})();
// Email du compte Supabase actuellement connecté (décodé depuis le JWT).
// Utilisé pour le suivi firstLogin par-email (profil partagé proprio + secrétaire).
export function currentAuthEmail(){
  try{
    if(!SUPA_TOKEN)return'';
    const payload=SUPA_TOKEN.split('.')[1];
    const decoded=JSON.parse(atob(payload.replace(/-/g,'+').replace(/_/g,'/')));
    return(decoded.email||'').trim().toLowerCase();
  }catch{return'';}
}
// Décode l'expiration (exp, secondes epoch) d'un JWT. null si indéchiffrable.
// Sert au refresh proactif : planifier le renouvellement AVANT que le token meure.
export function jwtExp(token){
  try{
    if(!token)return null;
    const payload=token.split('.')[1];
    const decoded=JSON.parse(atob(payload.replace(/-/g,'+').replace(/_/g,'/')));
    return(typeof decoded.exp==='number')?decoded.exp:null;
  }catch{return null;}
}
// Envoi de l'email « Définir mon mot de passe » à un client / accès secondaire.
// Appelle resetPasswordForEmail de Supabase : Supabase envoie son template (personnalisé)
// avec un lien sécurisé. redirectTo force le retour sur le domaine client officiel,
// quelle que soit l'adresse depuis laquelle l'admin déclenche l'envoi.
export async function sendAccessEmail(email){
  const clean=(email||'').trim().toLowerCase();
  if(!clean)throw new Error('Adresse email manquante.');
  const res=await fetch(`${SUPA_URL}/auth/v1/recover`,{
    method:'POST',
    headers:{'Content-Type':'application/json','apikey':SUPA_KEY},
    body:JSON.stringify({email:clean,redirect_to:CLIENT_APP_URL})
  });
  if(!res.ok){
    let msg='Échec de l\u2019envoi.';
    try{const d=await res.json();msg=d?.msg||d?.message||msg;}catch{}
    throw new Error(msg);
  }
  return true;
}
export function supaSaveSession(s){
  SUPA_TOKEN=s?.access_token||null;
  SUPA_REFRESH=s?.refresh_token||null;
  try{
    if(s)localStorage.setItem('supa_session',JSON.stringify({access_token:s.access_token,refresh_token:s.refresh_token,expires_at:s.expires_at}));
    else localStorage.removeItem('supa_session');
  }catch{}
  // Propager le token au client Supabase (pour Realtime / RLS sur les events)
  try{
    if(window.supaClient&&window.supaClient.realtime&&typeof window.supaClient.realtime.setAuth==='function'){
      window.supaClient.realtime.setAuth(SUPA_TOKEN||SUPA_KEY);
    }
  }catch(e){console.warn('[supaSaveSession] setAuth Realtime échoué',e);}
  // Refresh proactif : (re)planifie depuis le nouvel exp à chaque login/refresh ;
  // désarme le timer si s=null (déconnexion). Non bloquant.
  try{scheduleProactiveRefresh();}catch(e){console.warn('[supaSaveSession] planif refresh échouée',e);}
}
// Single-flight : une seule requête de refresh en vol à la fois. Sans ça, quand le token
// expire, la ~30aine de useSt (+ le timer proactif) partent en 401 simultané et appellent
// tous supaRefreshToken : le 1er fait tourner (rotate) le refresh token côté Supabase et
// invalide celui des concurrents → cascade "InvalidJWTToken" (la boucle observée sur kiosque).
// Ici, les appelants concurrents partagent LA MÊME promesse.
let _refreshPromise=null;
export async function supaRefreshToken(){
  if(!SUPA_REFRESH)return false;
  if(_refreshPromise)return _refreshPromise;
  _refreshPromise=(async()=>{
    try{
      const r=await fetch(`${SUPA_URL}/auth/v1/token?grant_type=refresh_token`,{
        method:'POST',
        headers:{'Content-Type':'application/json','apikey':SUPA_KEY},
        body:JSON.stringify({refresh_token:SUPA_REFRESH})
      });
      if(!r.ok)return false;
      const data=await r.json();
      supaSaveSession(data); // met à jour token + Realtime.setAuth + re-planifie
      return true;
    }catch{return false;}
    finally{_refreshPromise=null;}
  })();
  return _refreshPromise;
}

// ══════════════ REFRESH PROACTIF (anti-expiration silencieuse — cas kiosque) ══════════════
// Problème : le client est en autoRefreshToken:false et le refresh n'était que RÉACTIF
// (sur un 401 REST dans supaGet/supaSet). Sur une tablette kiosque ouverte 24/24 sans écrire,
// Realtime sain ⇒ polling suspendu ⇒ aucun trafic REST ⇒ rien ne déclenche le refresh :
// le token (~1 h) meurt, le Realtime se met en InvalidJWTToken/401, l'appli plante ~horaire.
// Fix : planifier un renouvellement ~90 s AVANT l'exp du token. supaSaveSession refait déjà
// realtime.setAuth ET re-planifie depuis le nouvel exp → le Realtime est ré-authentifié aussi.
const REFRESH_SKEW_MS=90000;    // on rafraîchit 90 s avant l'exp réelle (marge latence/throttling)
const REFRESH_RETRY_MS=30000;   // si le refresh échoue (réseau coupé) : nouvelle tentative dans 30 s
let _proactiveTimer=null;
async function _doProactiveRefresh(){
  _proactiveTimer=null;
  const ok=await supaRefreshToken();
  // Succès → supaSaveSession a déjà rappelé scheduleProactiveRefresh() avec le nouvel exp.
  // Échec (réseau, refresh token invalide…) → on retente bientôt, borné, jamais en boucle serrée.
  if(!ok&&typeof window!=='undefined'&&SUPA_TOKEN&&SUPA_REFRESH){
    _proactiveTimer=setTimeout(_doProactiveRefresh,REFRESH_RETRY_MS);
  }
}
// (Re)planifie le prochain refresh depuis l'exp du token courant. Idempotent (annule le timer
// précédent). No-op hors navigateur (renderToString) et si déconnecté (pas de token/refresh).
export function scheduleProactiveRefresh(){
  if(typeof window==='undefined')return;
  if(_proactiveTimer){clearTimeout(_proactiveTimer);_proactiveTimer=null;}
  if(!SUPA_TOKEN||!SUPA_REFRESH)return;      // déconnecté : rien à planifier
  const exp=jwtExp(SUPA_TOKEN);
  if(!exp)return;                            // exp illisible : on laisse le filet réactif 401 opérer
  const delay=exp*1000-Date.now()-REFRESH_SKEW_MS;
  if(delay<=0){_doProactiveRefresh();return;} // déjà dans la fenêtre (ou réveil après veille écran)
  _proactiveTimer=setTimeout(_doProactiveRefresh,delay);
}

// ══════════════ CLIENT SUPABASE (Realtime uniquement) ══════════════
// Exposé sur window.supaClient pour que realtime.js puisse y accéder
// ══════════════ CLIENT SUPABASE (Realtime uniquement) ══════════════
// Le reste de l'app reste en fetch brut via supaGet/supaSet/supaSetStrict (inchangé).
// Ce client sert UNIQUEMENT à ouvrir une connexion WebSocket Realtime sur la table app_data
// pour propager instantanément les modifs entre sessions (Claire + Franck, multi-onglets).
// On initialise au plus tôt, et on injecte le token utilisateur si on en a déjà un en localStorage.
export let supaClient=null;
try{
  if(window.supabase&&typeof window.supabase.createClient==='function'){
    supaClient=window.supabase.createClient(SUPA_URL,SUPA_KEY,{
      auth:{persistSession:false,autoRefreshToken:false,detectSessionInUrl:false}
    });
    window.supaClient=supaClient;
    // Si on a déjà un token chargé depuis localStorage, on l'envoie au Realtime maintenant.
    if(SUPA_TOKEN&&supaClient.realtime&&typeof supaClient.realtime.setAuth==='function'){
      supaClient.realtime.setAuth(SUPA_TOKEN);
    }
    console.log('[supaClient] SDK Supabase chargé, client prêt pour Realtime');
  }else{
    console.warn('[supaClient] SDK Supabase non chargé (CDN injoignable ?). Realtime désactivé, polling 20s assure la sync.');
  }
}catch(e){
  console.error('[supaClient] Erreur init client Supabase :',e);
}

// ══════════════ ARMEMENT DU REFRESH PROACTIF ══════════════
// (1) Chargement : si une session a été restaurée du localStorage (reload / reboot kiosque),
//     supaSaveSession n'a PAS été appelé → on planifie ici depuis le token restauré.
// (2) Réveil d'onglet / retour réseau : setTimeout est throttlé/suspendu écran éteint. À ces
//     moments on re-vérifie l'exp (event-driven, AUCUN polling idle → on respecte la contrainte
//     de volume de requêtes) et on refresh tout de suite si on est déjà dans la fenêtre de skew.
if(typeof window!=='undefined'){
  const wake=()=>{try{scheduleProactiveRefresh();}catch{}};
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)wake();});
  window.addEventListener('online',wake);
  window.addEventListener('focus',wake);
  try{scheduleProactiveRefresh();}catch(e){console.warn('[refresh proactif] planif initiale échouée',e);}
}

// ══════════════ STORE (fetch brut) ══════════════
// ══════════════ STORE (Supabase) ══════════════
export async function supaGet(key){
  // Note : on ne catch PAS les erreurs réseau ici. Si la lecture échoue,
  // useSt doit le savoir pour éviter d'écraser la base avec une valeur vide.
  // (v223) `order=updated_at.desc&limit=1` — garde-fou : si des lignes en
  // double existaient pour la même clé (signalé par Claire/Franck, cause
  // exacte en cours de vérification côté table), on prend TOUJOURS la plus
  // récente plutôt qu'une ligne arbitraire (comportement par défaut de
  // PostgREST sans ordre explicite).
  // (v226) CORRECTIF — `cache:'no-store'` : sans directive explicite, le
  // navigateur peut servir une réponse HTTP mise en cache pour une requête
  // GET identique, au lieu de retourner sur le réseau. Signalé par Claire/
  // Franck : un dépôt fraîchement écrit n'apparaissait plus occupé dès le
  // dépôt suivant (« fenêtre glissante » d'un seul élément) — exactement le
  // symptôme d'une lecture qui sert parfois une réponse périmée du cache au
  // lieu d'interroger la base à chaque appel.
  let r=await fetch(`${SUPA_URL}/rest/v1/app_data?key=eq.${key}&select=value&order=updated_at.desc&limit=1`,{
    cache:'no-store',
    headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`}
  });
  if(r.status===401&&SUPA_REFRESH&&await supaRefreshToken()){
    r=await fetch(`${SUPA_URL}/rest/v1/app_data?key=eq.${key}&select=value&order=updated_at.desc&limit=1`,{
      cache:'no-store',
      headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`}
    });
  }
  if(!r.ok)throw new Error('supaGet HTTP '+r.status+' for key '+key);
  const d=await r.json();
  return d?.[0]?.value??null;
}
export async function supaSet(key,value){
  try{
    let r=await fetch(`${SUPA_URL}/rest/v1/app_data`,{
      method:'POST',
      headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`,'Content-Type':'application/json','Prefer':'resolution=merge-duplicates'},
      body:JSON.stringify({key,value,updated_at:new Date().toISOString()})
    });
    if(r.status===401&&SUPA_REFRESH&&await supaRefreshToken()){
      await fetch(`${SUPA_URL}/rest/v1/app_data`,{
        method:'POST',
        headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`,'Content-Type':'application/json','Prefer':'resolution=merge-duplicates'},
        body:JSON.stringify({key,value,updated_at:new Date().toISOString()})
      });
    }
  }catch(e){console.error('supaSet error',e);}
}
// Bus global pour notifier les erreurs/succès de sync Supabase à travers l'app.
// Utilisé par useSt (pour signaler) et SyncErrorBanner (pour afficher).
export const syncBus=(typeof window!=='undefined')?(window.__lcSyncBus=window.__lcSyncBus||new EventTarget()):new EventTarget();


// ══════════════ STORE STRICT (throw sur erreur) ══════════════
export async function supaSetStrict(key,value){
  let r=await fetch(`${SUPA_URL}/rest/v1/app_data`,{
    method:'POST',
    headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`,'Content-Type':'application/json','Prefer':'resolution=merge-duplicates'},
    body:JSON.stringify({key,value,updated_at:new Date().toISOString()})
  });
  if(r.status===401&&SUPA_REFRESH&&await supaRefreshToken()){
    r=await fetch(`${SUPA_URL}/rest/v1/app_data`,{
      method:'POST',
      headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`,'Content-Type':'application/json','Prefer':'resolution=merge-duplicates'},
      body:JSON.stringify({key,value,updated_at:new Date().toISOString()})
    });
  }
  if(!r.ok){
    let corps='';
    try{corps=await r.text();}catch{}
    throw new Error('supaSetStrict HTTP '+r.status+' for key '+key+(corps?(' — '+corps.slice(0,300)):''));
  }
}

// ══════════════ ÉCRITURE SÛRE (lecture fraîche + fusion) ══════════════
// Pour les clés à enregistrement MUTABLE (ex. tc7 : un seul enregistrement par
// salarié-jour, modifié en place) — par opposition à als7/controles7 où chaque
// entrée est un événement atomique ajouté une fois pour toutes. `update(fresh)`
// doit renvoyer le nouveau tableau ; par convention (voir applyTcUpdate, utils.js),
// renvoyer LA MÊME référence que `fresh` signale « rien à écrire », et cette
// fonction s'abstient alors de tout appel réseau. Lève en cas d'échec réseau —
// à l'appelant de décider du repli (généralement retomber sur le setter classique
// de useSt pour bénéficier de son retry avec backoff).
export async function supaSetSafe(key,update){
  const fresh=await supaGet(key)||[];
  const merged=update(fresh);
  const changed=merged!==fresh;
  if(changed)await supaSetStrict(key,merged);
  return{merged,changed};
}

// ══════════════ SERVICE WORKER (notifications push) ══════════════
// ══════════════ SERVICE WORKER (commit 2.2 — notifications push) ══════════════
// On enregistre /sw.js qui tournera en arrière-plan pour recevoir les push
// même quand l'onglet est fermé. À ce stade, aucun push n'est encore envoyé
// par le serveur (ça viendra avec l'étape 5). Le SW est silencieux par design.
// Garde-fous :
//   - Vérifie que le navigateur supporte les SW (ignoré sur très vieux Safari).
//   - Erreur silencieuse : si l'enregistrement échoue, l'app fonctionne normalement.
if(typeof window!=='undefined'&&'serviceWorker' in navigator){
  window.addEventListener('load',()=>{
    navigator.serviceWorker.register('/sw.js')
      .then(reg=>{
        console.log('[SW] enregistré, scope :',reg.scope);
      })
      .catch(err=>{
        console.warn('[SW] enregistrement échoué (les notifs push seront indisponibles) :',err);
      });
  });
}

// Helper : compresse une image (File/Blob ou dataURL existante) en JPEG redimensionné.
// Critique pour rester sous la limite de 1MB de Supabase REST API ET pour ne pas faire planter
// l'onglet (crash "Aïe aïe aïe" sur Android) : une photo 12 Mpx décodée en bitmap = ~48 Mo, plus
// une dataURL base64 pleine taille = ~7 Mo ; empilés, ça tue le processus de rendu.
// Stratégie mémoire-safe pour les Blob/File (cas d'une photo prise par le salarié) :
//   createImageBitmap(blob) décode SANS dataURL intermédiaire et libère vite ; canvas.toBlob()
//   évite la grosse chaîne base64 de toDataURL() ; on libère explicitement bitmap + canvas.
// Pour une dataURL en entrée (recompression d'anciennes photos), on garde l'ancien chemin Image.

// ══════════════ UPLOADS PHOTOS & FACTURES ══════════════
export async function uploadDataUrlToStorage(dataUrl,path){
  // Convertir dataURL → Blob
  const resp=await fetch(dataUrl);
  const blob=await resp.blob();
  const ct=blob.type||'image/jpeg';
  const doUpload=()=>fetch(`${SUPA_URL}/storage/v1/object/rapports/${path}`,{
    method:'POST',
    headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`,'Content-Type':ct,'x-upsert':'false'},
    body:blob
  });
  // ─── RETRY : 3 tentatives avec backoff 1s / 2s / 4s ───
  // Objectif : absorber les coupures wifi passagères des salariés sur le terrain.
  // Sans ça, une photo qui rate reste en base64 dans rep7 → saturation et échec global de la sauvegarde.
  // On ne retente PAS sur 401 (géré séparément par refresh token) ni sur les 4xx non transitoires.
  const MAX=3;
  let lastErr=null;
  for(let attempt=0;attempt<MAX;attempt++){
    try{
      let r=await doUpload();
      if(r.status===401&&SUPA_REFRESH&&await supaRefreshToken()){r=await doUpload();}
      if(r.ok) return `${SUPA_URL}/storage/v1/object/public/rapports/${path}`;
      const txt=await r.text().catch(()=>'');
      lastErr=new Error('Storage upload HTTP '+r.status+' for '+path+(txt?' — '+txt.slice(0,200):''));
      // 4xx hors 408/429 = erreur définitive (mauvaise requête, conflit…) → inutile de retenter
      if(r.status>=400&&r.status<500&&r.status!==408&&r.status!==429) throw lastErr;
    }catch(e){
      // Erreur réseau (fetch rejeté : connexion coupée) → on retentera
      lastErr=e;
      if(/HTTP 4(?!08|29)/.test(e.message||'')) throw e; // 4xx définitif remonté plus haut
    }
    if(attempt<MAX-1){
      const delay=1000*Math.pow(2,attempt); // 1s, 2s, 4s
      console.warn(`[uploadDataUrlToStorage] tentative ${attempt+1}/${MAX} échouée pour ${path}, retry dans ${delay}ms`);
      await new Promise(res=>setTimeout(res,delay));
    }
  }
  throw lastErr||new Error('Storage upload échec après '+MAX+' tentatives pour '+path);
}

// ─── Pièces jointes messagerie interne (msgs7) ───
// Bucket public "rapports" (même bucket que les photos de rapport), préfixe messages/{tid}/...
// Décision Claire 12/08/2026 : un bucket privé dédié à la messagerie est un chantier à part,
// réfléchi plus tard — pour l'instant les pièces jointes de messages sont donc publiques,
// comme le sont déjà les photos de rapport dans ce même bucket.
// Images : compressées côté client (compressToDataUrl, mêmes réglages que les photos de
// rapport) puis uploadées via uploadDataUrlToStorage (retry 3x avec backoff déjà éprouvé).
// Documents non-image (PDF, etc.) : upload brut, sans compression, RETRY DUPLIQUÉ ici
// (même patron 3x/backoff qu'uploadDataUrlToStorage) plutôt qu'un refactor de cette dernière —
// fonction déjà utilisée par rep7/controles7/standards, mieux vaut ne pas y toucher pour ce
// chantier. Jamais de base64 stocké dans msgs7 (même principe que rep7/controles7 : un seul
// document JSON pour tous les messages, ne JAMAIS y embarquer une pièce jointe en base64).
export async function uploadMsgAttachment(file,tid){
  const rand=Math.random().toString(36).substring(2,12);
  const safeTid=String(tid).replace(/[^a-zA-Z0-9_-]/g,'_');
  const safeName=String(file.name||'fichier').replace(/[^a-zA-Z0-9._-]/g,'_').slice(0,80);
  const isImage=(file.type||'').startsWith('image/');
  if(isImage){
    const dataUrl=await compressToDataUrl(file,{maxDim:1600,quality:0.75});
    const baseName=safeName.replace(/\.[a-zA-Z0-9]+$/,'')||'photo';
    const path=`messages/${safeTid}/${rand}_${baseName}.jpg`;
    const url=await uploadDataUrlToStorage(dataUrl,path);
    return{url,name:file.name||'photo.jpg',type:'image/jpeg',size:dataUrlSizeKB(dataUrl)*1024};
  }
  const path=`messages/${safeTid}/${rand}_${safeName}`;
  const ct=file.type||'application/octet-stream';
  const doUpload=()=>fetch(`${SUPA_URL}/storage/v1/object/rapports/${path}`,{
    method:'POST',
    headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`,'Content-Type':ct,'x-upsert':'false'},
    body:file
  });
  const MAX=3;
  let lastErr=null;
  for(let attempt=0;attempt<MAX;attempt++){
    try{
      let r=await doUpload();
      if(r.status===401&&SUPA_REFRESH&&await supaRefreshToken()){r=await doUpload();}
      if(r.ok) return{url:`${SUPA_URL}/storage/v1/object/public/rapports/${path}`,name:file.name||'fichier',type:ct,size:file.size||null};
      const txt=await r.text().catch(()=>'');
      lastErr=new Error('Upload pièce jointe HTTP '+r.status+(txt?' — '+txt.slice(0,200):''));
      if(r.status>=400&&r.status<500&&r.status!==408&&r.status!==429) throw lastErr;
    }catch(e){
      lastErr=e;
      if(/HTTP 4(?!08|29)/.test(e.message||'')) throw e;
    }
    if(attempt<MAX-1){
      const delay=1000*Math.pow(2,attempt);
      await new Promise(res=>setTimeout(res,delay));
    }
  }
  throw lastErr||new Error('Upload pièce jointe échec après '+MAX+' tentatives pour '+path);
}

// ─── BUCKET PRIVÉ "factures" (justificatifs : relevés OTA, pièces comptables) ───
// Bucket NON public. On stocke uniquement le chemin (path) dans l'objet facture.
// La lecture passe par une URL signée à durée limitée (générée à la demande).
// Path : `{cid}/{annee}/{rand}_{nomFichier}` → non devinable + classé par proprio/année.
export async function uploadFileToFacturesBucket(file,cid,annee){
  const rand=Math.random().toString(36).substring(2,12);
  const safeName=String(file.name||'fichier').replace(/[^a-zA-Z0-9._-]/g,'_').slice(0,80);
  const path=`${cid}/${annee}/${rand}_${safeName}`;
  const ct=file.type||'application/octet-stream';
  const doUpload=()=>fetch(`${SUPA_URL}/storage/v1/object/factures/${path}`,{
    method:'POST',
    headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`,'Content-Type':ct,'x-upsert':'false'},
    body:file
  });
  let r=await doUpload();
  if(r.status===401&&SUPA_REFRESH&&await supaRefreshToken()){r=await doUpload();}
  if(!r.ok){
    const txt=await r.text().catch(()=>'');
    throw new Error('Upload justificatif HTTP '+r.status+(txt?' — '+txt.slice(0,200):''));
  }
  return path;
}

// Génère une URL signée (valable expiresIn secondes, défaut 1h) pour lire un justificatif privé.
export async function getSignedFactureUrl(path,expiresIn=3600){
  const doSign=()=>fetch(`${SUPA_URL}/storage/v1/object/sign/factures/${path}`,{
    method:'POST',
    headers:{'apikey':SUPA_KEY,'Authorization':`Bearer ${supaAuthHeader()}`,'Content-Type':'application/json'},
    body:JSON.stringify({expiresIn})
  });
  let r=await doSign();
  if(r.status===401&&SUPA_REFRESH&&await supaRefreshToken()){r=await doSign();}
  if(!r.ok){
    const txt=await r.text().catch(()=>'');
    throw new Error('URL signée HTTP '+r.status+(txt?' — '+txt.slice(0,200):''));
  }
  const j=await r.json();
  // L'API renvoie signedURL sous la forme "/object/sign/factures/...?token=..."
  // SANS le préfixe "/storage/v1". On le réinsère pour obtenir une URL complète valide.
  let s=j.signedURL||j.signedUrl||'';
  if(s.startsWith('/storage/v1')){
    return `${SUPA_URL}${s}`;
  }
  if(s.startsWith('/')){
    return `${SUPA_URL}/storage/v1${s}`;
  }
  // format absolu inattendu : on le renvoie tel quel
  return s.startsWith('http')?s:`${SUPA_URL}/storage/v1/${s}`;
}

// Convertit un objet { key: dataURL } en { key: URL Storage }.
// Les valeurs déjà en URL (http://) ou non-image sont laissées telles quelles (idempotent).
// En cas d'échec d'upload d'une photo, on garde le dataURL original (mieux que perdre la photo).
export async function uploadPhotosToStorage(obj,repId,prefix){
  const out={};
  for(const[k,v]of Object.entries(obj||{})){
    if(typeof v==='string'&&v.startsWith('data:image')){
      const rand=Math.random().toString(36).substring(2,12);
      const safeKey=String(k).replace(/[^a-zA-Z0-9_-]/g,'_');
      const path=`${repId}/${prefix}_${safeKey}_${rand}.jpg`;
      try{out[k]=await uploadDataUrlToStorage(v,path);}
      catch(e){console.error('[uploadPhotosToStorage] failed for',k,e);out[k]=v;}
    }else{out[k]=v;}
  }
  return out;
}

// ─── Photo de référence d'un standard propriétaire (SPEC §12.6) ───
// Durable (ne change pas à chaque ménage) : NE PAS mélanger avec les photos de rapport
// (jetables, dossier {repId}/...). Même bucket "rapports" (aucun bucket dédié à créer côté
// Supabase), mais préfixe séparé standards/{bienId}/... pour l'isolation logique.
export async function uploadStandardPhoto(dataUrl,bienId,itemId){
  const rand=Math.random().toString(36).substring(2,12);
  const safeItemId=String(itemId).replace(/[^a-zA-Z0-9_-]/g,'_');
  const path=`standards/${bienId}/${safeItemId}_${rand}.jpg`;
  return uploadDataUrlToStorage(dataUrl,path);
}

// ══════════════ MIGRATION PHOTOS BASE64 ══════════════
export function countBase64Photos(rapports){
  let n=0;
  for(const r of(rapports||[])){
    for(const v of Object.values(r.pk||{}))if(typeof v==='string'&&v.startsWith('data:image'))n++;
    for(const v of Object.values(r.gp||{}))if(typeof v==='string'&&v.startsWith('data:image'))n++;
  }
  return n;
}

// Migration ponctuelle des photos anciennes : parcourt rep7, uploade chaque photo base64
// vers Storage et remplace par son URL. Idempotent (peut être relancé sans risque).
// onProgress({processed,total,currentRep}) appelé après chaque rapport.
// Note : initialement appliquée aussi sur rep7_archive_20260524 (incident du 24/05).
// Cette archive a été fusionnée dans rep7 puis supprimée en mai 2026 → plus besoin.
export async function migrateAllPhotosToStorage(onProgress){
  const baseData=(await supaGet('rep7'))||[];
  const total=countBase64Photos(baseData);
  let processed=0;
  const processOne=async rapports=>{
    const out=[];
    for(const r of rapports){
      const before=countBase64Photos([r]);
      const newPk=await uploadPhotosToStorage(r.pk||{},r.id,'pk');
      const newGp=await uploadPhotosToStorage(r.gp||{},r.id,'gp');
      const after=countBase64Photos([{pk:newPk,gp:newGp}]);
      processed+=(before-after);
      onProgress&&onProgress({processed,total,currentRep:r.id});
      out.push({...r,pk:newPk,gp:newGp});
    }
    return out;
  };
  const migrated=await processOne(baseData);
  // ─── Read-before-write final (Option C, correctif 06/09/2026) ───────────────
  // La migration peut durer plusieurs minutes. Pendant ce temps, de nouveaux
  // rapports peuvent être soumis. Un supaSetStrict('rep7', migrated) en aveugle
  // écraserait ces rapports. On relit la base fraîche et on fusionne par id :
  // les rapports arrivés pendant la migration sont préservés, et chaque rapport
  // migré remplace son ancienne version (urls Storage au lieu de base64).
  const freshAfter=(await supaGet('rep7'))||[];
  const migratedById=Object.fromEntries(migrated.map(r=>[r.id,r]));
  // On part de la version fraîche (qui peut avoir de nouveaux rapports), on y
  // injecte les versions migrées pour les rapports traités, on append les rapports
  // de la base fraîche qui n'étaient PAS dans notre lot de migration (ne devrait
  // pas arriver mais c'est un garde-fou de sécurité).
  const freshIds=new Set(freshAfter.map(r=>r.id));
  const migratedIds=new Set(migrated.map(r=>r.id));
  // Rapports dans la base fraîche : on prend la version migrée si elle existe, sinon on garde la fraîche.
  const merged=freshAfter.map(r=>migratedById[r.id]||r);
  // Rapports migrés qui ne sont plus dans la base fraîche (supprimés entre-temps) : on les ignore.
  // Rapports dans la base fraîche qui n'étaient pas dans baseData (nouveaux arrivés pendant la migration) : déjà préservés par la ligne ci-dessus.
  await supaSetStrict('rep7',merged);
  // Nombre de photos qui n'ont pas pu être uploadées (restent en base64)
  const remaining=countBase64Photos(merged.filter(r=>migratedIds.has(r.id)));
  return{total,migrated:processed,errors:remaining};
}

// ══════════════ MIGRATION PHOTOS — CONTRÔLES QUALITÉ (controles7) ══════════════
// Diagnostiqué session du 11/08/2026 : contrairement à rep7, les photos de contrôle n'ont
// JAMAIS été migrées vers Storage — elles restent en base64 dans l'unique document controles7
// (une ligne app_data, tout l'historique de la saison), qui grossit sans limite à chaque
// contrôle → sauvegardes qui échouent ou traînent, pire sur connexion terrain faible. Cause
// racine du symptôme « le contrôle n'avance pas, on en perd » remonté par Claire. Structure
// différente de rep7 (pk/gp, objets id→dataURL) : ici payload.pieces[pieceId].photos est un
// TABLEAU de dataURL par pièce — d'où le passage par un objet indexé pour réutiliser
// uploadPhotosToStorage tel quel (pas de duplication de la logique d'upload/retry).
export function countBase64PhotosCtrl(controlesList){
  let n=0;
  for(const c of(controlesList||[])){
    for(const p of Object.values(c.pieces||{})){
      for(const v of(p&&p.photos||[]))if(typeof v==='string'&&v.startsWith('data:image'))n++;
    }
  }
  return n;
}
// Upload d'un tableau de photos (une pièce de contrôle) vers Storage. Même garantie que
// uploadPhotosToStorage : une photo qui échoue reste en base64, jamais d'exception remontée.
export async function uploadCtrlPieceArray(photos,ctrlId,pieceId){
  const asObj=Object.fromEntries((photos||[]).map((v,i)=>[String(i),v]));
  const out=await uploadPhotosToStorage(asObj,ctrlId,`piece_${pieceId}`);
  return(photos||[]).map((_,i)=>out[String(i)]);
}
// Migration ponctuelle des contrôles déjà en base : parcourt controles7, uploade chaque photo
// base64 vers Storage et remplace par son URL. Idempotent (peut être relancé sans risque).
export async function migrateAllPhotosToStorageCtrl(onProgress){
  const baseData=(await supaGet('controles7'))||[];
  const total=countBase64PhotosCtrl(baseData);
  let processed=0;
  const out=[];
  for(const c of baseData){
    const before=countBase64PhotosCtrl([c]);
    const newPieces={};
    for(const[pieceId,p]of Object.entries(c.pieces||{})){
      const newPhotos=await uploadCtrlPieceArray((p&&p.photos)||[],c.id,pieceId);
      newPieces[pieceId]={...p,photos:newPhotos};
    }
    const after=countBase64PhotosCtrl([{pieces:newPieces}]);
    processed+=(before-after);
    onProgress&&onProgress({processed,total,currentCtrl:c.id});
    out.push({...c,pieces:newPieces});
  }
  await supaSetStrict('controles7',out);
  const remaining=countBase64PhotosCtrl(out);
  return{total,migrated:processed,errors:remaining};
}

// ══════════════ HOOK useSt — state synchronisé avec Supabase ══════════════
export function useSt(k,i,opts){
  const _defer=!!(opts&&opts.defer);
  const[v,sv]=useState(i);
  const ready=useRef(false);
  // lastSync = JSON.stringify de la dernière valeur réellement synchronisée avec Supabase
  // (soit lue au boot, soit écrite après une modif locale).
  // Sert à : (a) skipper les écritures redondantes, (b) détecter les vrais changements.
  const lastSync=useRef(null);
  const[loaded,setLoaded]=useState(false);
  // Ref vers la valeur la plus récente (utilisée par les retries d'écriture pour ne pas envoyer une valeur périmée)
  const currentValue=useRef(v);
  const initialSerialized=useRef(JSON.stringify(i));
  // Flag : l'utilisateur a-t-il modifié la valeur localement AVANT que la lecture initiale réussisse ?
  // Si oui, on doit préserver ses modifs et les pousser en base dès que la sync est OK.
  const localChangedBeforeReady=useRef(false);

  useEffect(()=>{
    currentValue.current=v;
    if(!ready.current && JSON.stringify(v)!==initialSerialized.current){
      localChangedBeforeReady.current=true;
    }
  },[v]);

  // Tente d'écrire en base avec retry automatique (backoff exponentiel 2s, 4s, 8s, 16s, 30s max)
  const trySave=(valueAtCall,attempt=0)=>{
    supaSetStrict(k,valueAtCall).then(()=>{
      // Succès : notifier le bus pour effacer une éventuelle bannière d'erreur
      syncBus.dispatchEvent(new CustomEvent('ok',{detail:{key:k}}));
      // File d'attente locale (SPEC_file_locale_v1 §5.3) : confirmer les entrées rep7
      // dont le recordId est réellement présent dans la valeur écrite. Non bloquant.
      if(k==='rep7'&&Array.isArray(valueAtCall)){
        try{confirmByRecordIds('rep7',valueAtCall.map(r=>r&&r.id).filter(Boolean));}
        catch(err){console.error('[useSt] confirmation file locale échouée (non bloquant)',err);}
      }
      // Chantier B (01/08/2026) : même principe pour controles7. Le badge vert du
      // SyncErrorBanner ne garantit pas QUE cette écriture précise a réussi (il
      // suffit qu'UNE écriture sur la clé réussisse) — mais la confirmation par
      // recordId, elle, est fiable : elle ne marque "confirmé" que les contrôles
      // dont l'id est réellement présent dans la valeur qui vient d'être écrite.
      // C'est cette confirmation-là (pas le bandeau) qui protège réellement la donnée.
      if(k==='controles7'&&Array.isArray(valueAtCall)){
        try{confirmByRecordIds('controles7',valueAtCall.map(c=>c&&c.id).filter(Boolean));}
        catch(err){console.error('[useSt] confirmation file locale (controles7) échouée (non bloquant)',err);}
      }
    }).catch(e=>{
      console.error('[useSt] Écriture échouée pour',k,'tentative',attempt+1,':',e.message);
      // Notifier le bus pour afficher la bannière d'erreur
      syncBus.dispatchEvent(new CustomEvent('error',{detail:{key:k,message:e.message,attempt:attempt+1,op:'write'}}));
      const delay=Math.min(2000*Math.pow(2,attempt),30000);
      setTimeout(()=>{
        // On envoie toujours la valeur la plus récente (peut avoir changé entre temps)
        trySave(currentValue.current,attempt+1);
      },delay);
    });
  };

  // Tente de lire depuis la base avec retry automatique
  const tryLoad=(attempt=0)=>{
    if(k==='sess7'){ready.current=true;setLoaded(true);return;}
    supaGet(k).then(val=>{
      // Lecture réussie : notifier OK
      syncBus.dispatchEvent(new CustomEvent('ok',{detail:{key:k}}));
      if(localChangedBeforeReady.current){
        // L'utilisateur a déjà fait des modifs avant que la lecture aboutisse :
        // on PRÉSERVE ses modifs et on les pousse en base immédiatement.
        const localValue=currentValue.current;
        lastSync.current=JSON.stringify(localValue);
        ready.current=true;
        setLoaded(true);
        trySave(localValue);
        console.log('[useSt] Lecture réussie pour',k,'après modifs locales — préservation et écriture');
      }else if(val!==null){
        sv(val);
        lastSync.current=JSON.stringify(val);
        ready.current=true;
        setLoaded(true);
      }else{
        // Aucune ligne en base pour cette clé : on garde la valeur initiale i.
        lastSync.current=JSON.stringify(i);
        ready.current=true;
        setLoaded(true);
      }
    }).catch(e=>{
      console.error('[useSt] Lecture échouée pour',k,'tentative',attempt+1,':',e.message);
      syncBus.dispatchEvent(new CustomEvent('error',{detail:{key:k,message:e.message,attempt:attempt+1,op:'read'}}));
      const delay=Math.min(2000*Math.pow(2,attempt),30000);
      setTimeout(()=>tryLoad(attempt+1),delay);
      // On libère le loader UI dès la 1ère tentative (pour ne pas bloquer indéfiniment),
      // mais les écritures restent gelées tant que la lecture n'a pas réussi.
      // Les modifs locales seront poussées en base dès que la lecture finira par réussir.
      if(attempt===0)setLoaded(true);
    });
  };

  useEffect(()=>{
    // Clés lourdes non critiques au boot (sejours, factures…) : on diffère le chargement
    // pour ne pas saturer la mémoire au démarrage sur les appareils à faible plafond (ex. 919 Mo).
    if(_defer){const t=setTimeout(()=>tryLoad(),4000);return()=>clearTimeout(t);}
    tryLoad();
  },[]);

  useEffect(()=>{
    if(!ready.current)return;
    if(k==='sess7')return;
    const cur=JSON.stringify(v);
    if(cur===lastSync.current)return; // pas de vrai changement : on n'écrit pas
    lastSync.current=cur;
    trySave(v);
  },[v]);

  // ─── REALTIME (commit 3) ───
  // On écoute le realtimeBus pour les changements de cette clé en particulier.
  // Quand un autre client modifie app_data[k], on reçoit l'event quasi-instantanément
  // (vs 20s avec le polling). Même logique anti-écrasement que le polling :
  //   - si remote == lastSync → rien à faire (on est déjà à jour)
  //   - si local != lastSync → modif locale en cours, on s'abstient (trySave la poussera)
  //   - sinon → modif distante, on rafraîchit le state local
  // Le polling 20s reste en place comme filet de sécurité (si Realtime décroche).
  //
  // (v227) `opts.realtime===false` — désactive cette écoute pour CET appel de
  // useSt uniquement (les autres appels sur la même clé, ailleurs dans
  // l'appli, restent inchangés). Ajouté pour l'écran comptoir : signalé par
  // Claire/Franck, un dépôt fraîchement affiché localement (setSynced,
  // v225) pouvait être écrasé par un événement Realtime ARRIVÉ EN RETARD ou
  // DANS LE DÉSORDRE — cette logique compare seulement « est-ce différent de
  // ce que j'ai déjà », jamais « est-ce plus RÉCENT », donc un événement
  // tardif d'une écriture plus ancienne peut écraser un état local plus
  // frais avec une version périmée. Le comptoir n'a pas besoin de Realtime
  // pour ses propres écritures (déjà réverbérées instantanément via
  // setSynced) ; le désactiver élimine ce risque sans rien perdre.
  useEffect(()=>{
    if(k==='sess7')return; // jamais de realtime sur la session
    if(opts&&opts.realtime===false)return; // (v227) désactivé pour cet appel
    const handler=(e)=>{
      if(!ready.current)return; // lecture initiale pas finie : on ignore
      const remote=e?.detail?.value;
      if(remote===null||remote===undefined)return; // DELETE ou payload vide : on ignore
      try{
        const remoteSer=JSON.stringify(remote);
        const localSer=JSON.stringify(currentValue.current);
        if(remoteSer===lastSync.current)return; // déjà à jour
        if(localSer!==lastSync.current)return;  // modif locale en attente, ne pas écraser
        console.log('[useSt] Realtime',k,': nouvelle version reçue, mise à jour locale (instantanée)');
        lastSync.current=remoteSer;
        sv(remote);
      }catch(err){
        console.error('[useSt] Realtime handler error pour',k,':',err);
      }
    };
    const evtName='change:'+k;
    realtimeBus.addEventListener(evtName,handler);
    return()=>realtimeBus.removeEventListener(evtName,handler);
  },[k]);

  // ─── AUTO-REFRESH MULTI-SESSION ───
  // Filet de sécurité derrière Realtime, pour détecter les modifs faites par d'autres sessions
  // (ex: l'autre admin sur un autre ordi, ou un salarié qui pointe).
  // Logique : si la valeur en base ≠ lastSync ET qu'on n'a pas de modif locale en attente,
  //          alors quelqu'un d'autre a modifié → on rafraîchit le state local.
  //
  // ⚠️ Ce polling ne s'exécute QUE si Realtime est en panne (window.__lcRealtimeHealthy===false).
  // Historique : jusqu'au 23/07/2026 il tournait à 20s en parallèle de Realtime, sur ~30 clés,
  // soit 90 req/min par onglet ouvert → ~35 000 req/jour → saturation RAM de l'instance Supabase
  // (statut Unhealthy, erreurs 522). Realtime couvre déjà la sync multi-session ; ce tick est
  // redondant tant que le canal WebSocket répond.
  //
  // Trois gardes cumulés :
  //   1. onglet caché → on saute (économise batterie/data)
  //   2. Realtime sain → on saute, SAUF vérification de cohérence toutes les 5 min
  //      (protège contre un canal qui reste SUBSCRIBED mais ne délivre plus d'events)
  //   3. lecture initiale pas finie → on saute
  useEffect(()=>{
    if(k==='sess7')return; // jamais de poll sur la session
    if(opts&&opts.realtime===false)return; // (v227) même désactivation que Realtime, même raison
    let stopped=false;
    const POLL_MS=60000;          // 60s en mode dégradé (Realtime HS)
    const SANITY_MS=300000;       // 5 min : vérification de cohérence même si Realtime est sain
    let lastCheck=Date.now();
    const tick=async()=>{
      if(stopped)return;
      if(document.hidden){return;} // onglet caché : on saute ce tick
      if(!ready.current)return;     // lecture initiale pas encore réussie : on saute
      // Realtime opérationnel → on s'abstient, sauf si la dernière vérification date de +5 min
      if(window.__lcRealtimeHealthy===true&&(Date.now()-lastCheck)<SANITY_MS)return;
      lastCheck=Date.now();
      try{
        const remote=await supaGet(k);
        if(stopped)return;
        if(remote===null)return; // pas encore de ligne en base
        const remoteSer=JSON.stringify(remote);
        const localSer=JSON.stringify(currentValue.current);
        // Si remote == ce qu'on a déjà synchronisé → rien à faire
        if(remoteSer===lastSync.current)return;
        // Si on a une modif locale non encore synchronisée (local ≠ lastSync) :
        // on ne veut PAS l'écraser. On laisse trySave la pousser, puis on re-sync au prochain tick.
        if(localSer!==lastSync.current)return;
        // Sinon : quelqu'un d'autre a modifié la base → on rafraîchit le state local
        console.log('[useSt] Auto-refresh',k,': nouvelle version détectée en base, mise à jour locale');
        lastSync.current=remoteSer;
        sv(remote);
      }catch(e){
        // Erreur réseau silencieuse pendant le poll : on retentera au tick suivant
      }
    };
    // Décalage aléatoire 0–5s au démarrage : sans ça les ~30 instances de useSt tirent
    // exactement à la même seconde et créent un pic de charge instantané sur l'instance.
    let iv=null;
    const startDelay=Math.floor(Math.random()*5000);
    const startTimer=setTimeout(()=>{if(!stopped)iv=setInterval(tick,POLL_MS);},startDelay);
    // Refresh aussi quand l'onglet redevient visible (utile après une pause longue).
    // On force la vérification en réinitialisant lastCheck : au retour d'une mise en veille
    // prolongée, Realtime peut avoir manqué des events sans être passé en CHANNEL_ERROR.
    const onVis=()=>{if(!document.hidden){lastCheck=0;tick();}};
    document.addEventListener('visibilitychange',onVis);
    return()=>{stopped=true;clearTimeout(startTimer);if(iv)clearInterval(iv);document.removeEventListener('visibilitychange',onVis);};
  },[k]);

  // Chantier B (01/08/2026) : setSynced — pour les appelants qui viennent d'écrire
  // EUX-MÊMES avec succès (ex. punch() dans GerantUI.jsx/PointageTerrain.jsx, qui a
  // son propre supaGet/supaSet en lecture-avant-écriture pour la sécurité multi-session).
  // setAls(v) classique redéclenche `trySave` via l'effet ci-dessus (v a changé) — une
  // 2e écriture réseau redondante et non mergée, en parallèle de celle que l'appelant
  // vient de faire. setSynced met à jour l'état local ET lastSync.current ENSEMBLE,
  // pour que l'effet [v] ne voie aucun "vrai changement" et ne reparte pas en écriture.
  const setSynced=(newVal)=>{
    // (v229) CORRECTIF — CAUSE RÉELLE de la « fenêtre glissante d'un seul
    // dépôt » (signalé par Claire/Franck, confirmé par le journal de
    // diagnostic v228). `setSynced` mettait à jour `v`/`lastSync` mais
    // JAMAIS `ready.current`. Pour les clés différées (`{defer:true}`,
    // comme consigneDepots7/consigneLocations7), `tryLoad()` ne se lance
    // qu'après 4s — si le staff valide un dépôt AVANT cette échéance,
    // `ready.current` est encore `false` au moment de CET appel. L'autre
    // effet ([v]) voit alors `!ready.current` et marque
    // `localChangedBeforeReady.current=true`. Quand le `tryLoad()` différé
    // finit par s'exécuter, il prend la branche « préserver les modifs
    // locales » : il écrit CETTE valeur locale — qui ne contient QUE ce que
    // CETTE session a créé, pas les dépôts déjà présents en base avant son
    // chargement — via `trySave` (écriture DIRECTE, pas atomique), ÉCRASANT
    // le tableau complet côté serveur avec ce sous-ensemble. Le dépôt
    // suivant, en relisant fraîchement la base pour construire son propre
    // affichage, ne retrouve alors plus que ce sous-ensemble incomplet.
    // Marquer `ready.current=true` ICI évite que ce chemin destructeur se
    // déclenche : le prochain `tryLoad()` différé prendra la branche
    // normale (`sv(val)` avec le VRAI contenu serveur), jamais un
    // écrasement basé sur un instantané local partiel.
    ready.current=true;
    lastSync.current=JSON.stringify(newVal);
    sv(newVal);
  };

  return[v,sv,loaded,setSynced];
}

// ══════════════ ICONS ══════════════

// ══════════════ EDITION POINTAGE ADMIN (modifs traçables) ══════════════

// ─── Génération PDF historique journée pour un salarié ───
// Produit un PDF synthétique de la journée pour pouvoir le présenter au salarié.
// Inclut : récap journée, détail pause par pause, intervention par appartement, écarts, géo, modifications admin, rappel réglementaire.
